import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Send,
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Check,
  Undo2,
  Copy,
  Trash2,
  Bot,
  User,
  Wand2,
  Expand,
  Shrink,
  CheckCircle,
  MessageSquare,
  Type,
  BookOpen,
  PenLine,
  Lightbulb,
  FileSearch,
  RefreshCw,
  Feather,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { notify } from '../../lib/toast';
import { useAIStore, type ChatMessage, type AIAction } from '../../stores/ai-store';
import { useAIQualityStore, AI_MODEL_MAP, type AIQuality } from '../../stores/ai-quality-store';

/** MANGA/HQ conversational mode — the SAME chat UI, but the assistant fills the
 * comic PAGE script, the Universe (cast) and the premise, all reactive. When
 * present, the chat runs in manga mode: manga context is sent to the server and
 * the manga action types are applied through these callbacks. */
export interface MangaChatMode {
  /** Serialized script of the CURRENT page (QUADRO N blocks) — the working doc. */
  pageScript: string;
  pageNumber: number;
  totalPages: number;
  /** Canonical cast names (one per line) — the Universe roster for this project. */
  castNames: string[];
  premise: string;
  /** Apply a full page script the assistant wrote (parse + reconcile + persist). */
  onApplyPageScript: (script: string) => void | Promise<void>;
  /** Create a new Universe entry (character/location). */
  onCreateCharacter: (data: { name: string; characterType: string; description: string }) => Promise<void>;
  /** Replace the project's premise. */
  onSetPremise: (premise: string) => Promise<void>;
}

interface AIChatProps {
  chapterContent: string;
  selection?: string;
  onApplyAction: (action: AIAction) => void;
  onClose: () => void;
  projectId: string;
  currentChapterId?: string;
  chapters?: Array<{ id: string; title: string; sectionType: string; content?: string }>;
  totalWords?: number;
  isAnalyzing?: boolean;
  /** When true, renders inner content only (no wrapper/animation) for use inside a vaul Drawer. */
  embedded?: boolean;
  /** When set, the chat runs in MANGA mode (see MangaChatMode). */
  manga?: MangaChatMode;
  onCreateChapter?: (data: {
    title: string;
    content?: string;
    position?: 'after_current' | 'before_current' | 'end' | 'start';
    sectionType?: string;
  }) => Promise<void>;
  onUpdateChapter?: (id: string, data: { title?: string }) => Promise<void>;
  onDeleteChapter?: (id: string) => Promise<void>;
  onReorderChapters?: (ids: string[]) => Promise<void>;
  onSelectChapter?: (id: string) => void;
}

// Contextual chip types
type ContextualChip = {
  id: string;
  label: string;
  icon: React.ElementType;
  action: string;
  description?: string;
};

// Quick action type
type QuickAction = 'improve' | 'expand' | 'condense' | 'grammar';

export function AIChat({
  chapterContent,
  selection,
  onApplyAction,
  onClose,
  projectId,
  currentChapterId,
  chapters = [],
  totalWords,
  isAnalyzing = false,
  embedded = false,
  manga,
  onCreateChapter,
  onUpdateChapter,
  onDeleteChapter,
  onReorderChapters,
  onSelectChapter,
}: AIChatProps) {
  const { t } = useTranslation();
  const { quality: aiQuality, setQuality: setAiQuality } = useAIQualityStore();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [quickActionLoading, setQuickActionLoading] = useState<QuickAction | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Calculate book stats
  const bookStats = useMemo(() => {
    const wordCount = totalWords ?? chapters.reduce((acc, ch) => {
      const content = ch.content || '';
      return acc + content.split(/\s+/).filter(Boolean).length;
    }, 0);
    return {
      chapters: chapters.length,
      words: wordCount,
      formattedWords: wordCount >= 1000
        ? `${(wordCount / 1000).toFixed(1)}k`
        : wordCount.toString(),
    };
  }, [chapters, totalWords]);

  // Get contextual chips based on current state
  const contextualChips = useMemo((): ContextualChip[] => {
    // MANGA mode — chips drive the page/Universe/premise instead of chapters.
    if (manga) {
      return [
        { id: 'mg-fill', label: t('ai.mangaFillPage', 'Preencher página'), icon: PenLine, action: 'manga_fill_page', description: t('ai.mangaFillPageDesc', 'Escreva o roteiro desta página inteira') },
        { id: 'mg-char', label: t('ai.mangaNewCharacter', 'Novo personagem'), icon: User, action: 'manga_new_character', description: t('ai.mangaNewCharacterDesc', 'Criar alguém no Universo') },
        { id: 'mg-next', label: t('ai.mangaWhatNext', 'E agora?'), icon: Lightbulb, action: 'manga_what_next', description: t('ai.mangaWhatNextDesc', 'Sugira o próximo beat da história') },
      ];
    }

    const hasContent = chapterContent.trim().length > 0;

    if (selection) {
      // Chips for selected text
      return [
        { id: 'expand', label: t('ai.expand', 'Expandir'), icon: Expand, action: 'expand', description: t('ai.expandDesc', 'Adicionar mais detalhes') },
        { id: 'rewrite', label: t('ai.rewrite', 'Reescrever'), icon: RefreshCw, action: 'rewrite', description: t('ai.rewriteDesc', 'Reescrever de forma diferente') },
        { id: 'check', label: t('ai.checkFacts', 'Verificar'), icon: FileSearch, action: 'check_facts', description: t('ai.checkFactsDesc', 'Verificar fatos') },
      ];
    }

    if (!hasContent) {
      // Empty chapter chips
      return [
        { id: 'start', label: t('ai.startWriting', 'Começar a escrever'), icon: PenLine, action: 'start_writing', description: t('ai.startWritingDesc', 'Ajude-me a começar este capítulo') },
        { id: 'outline', label: t('ai.seeOutline', 'Ver estrutura'), icon: BookOpen, action: 'see_outline', description: t('ai.seeOutlineDesc', 'Mostrar estrutura do capítulo') },
        { id: 'brainstorm', label: t('ai.brainstorm', 'Brainstorm'), icon: Lightbulb, action: 'brainstorm', description: t('ai.brainstormDesc', 'Gerar ideias') },
      ];
    }

    // Chapter with content chips
    return [
      { id: 'expand_chapter', label: t('ai.expandChapter', 'Expandir capítulo'), icon: Expand, action: 'expand_chapter', description: t('ai.expandChapterDesc', 'Adicionar mais a este capítulo') },
      { id: 'consistency', label: t('ai.checkConsistency', 'Verificar consistência'), icon: FileSearch, action: 'check_consistency', description: t('ai.checkConsistencyDesc', 'Verificar consistência narrativa') },
      { id: 'next_step', label: t('ai.nextStep', 'Próxima etapa'), icon: Feather, action: 'next_step', description: t('ai.nextStepDesc', 'Sugerir o que escrever a seguir') },
    ];
  }, [selection, chapterContent, t, manga]);

  const {
    messages,
    addMessage,
    clearMessages,
    isRecording,
    setRecording,
    applyAction,
    revertAction,
    setChapterContent,
    setSelection,
  } = useAIStore();

  // Update context when props change
  useEffect(() => {
    setChapterContent(chapterContent);
    if (selection) {
      setSelection(selection);
    }
  }, [chapterContent, selection, setChapterContent, setSelection]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus the input on mount ONLY on the desktop side panel. In the mobile
  // drawer (embedded) we must NOT auto-focus — it would pop the keyboard the
  // moment the drawer opens, which the user explicitly doesn't want.
  useEffect(() => {
    if (!embedded) inputRef.current?.focus();
  }, [embedded]);

  // Cleanup recording interval on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);

  // Format recording elapsed time as M:SS
  const formatRecordingTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Build chat history for API
  const buildHistory = useCallback(() => {
    return messages.slice(-10).map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }));
  }, [messages]);

  // Quick actions for selected text
  const handleQuickAction = async (action: QuickAction) => {
    if (!selection || quickActionLoading) return;

    setQuickActionLoading(action);

    try {
      // Pass projectId so the rewrite consults the Story Bible (RAG) for consistency.
      const { result } = await api.processText(selection, action, undefined, projectId);
      onApplyAction({
        type: 'replace',
        searchText: selection,
        newText: result,
      });
      notify.success(t('ai.operationComplete', 'Texto processado'));
    } catch (error: any) {
      if (error.status === 429) {
        notify.error(t('ai.quotaExceeded', 'Cota de IA excedida'));
      } else {
        notify.error(t('ai.operationFailed', 'Falha ao processar'));
      }
    } finally {
      setQuickActionLoading(null);
    }
  };

  // Handle contextual chip click
  const handleChipClick = (chip: ContextualChip) => {
    // Build contextual message based on chip action
    const messages: Record<string, string> = {
      'expand': t('ai.chipExpand', 'Por favor, expanda este texto com mais detalhes sensoriais e tensão'),
      'rewrite': t('ai.chipRewrite', 'Por favor, reescreva este texto em um estilo diferente'),
      'check_facts': t('ai.chipCheckFacts', 'Por favor, verifique os fatos mencionados neste texto'),
      'start_writing': t('ai.chipStartWriting', 'Ajude-me a começar a escrever este capítulo. Sugira 3 aberturas diferentes com base no contexto do meu livro'),
      'see_outline': t('ai.chipSeeOutline', 'Mostre-me a estrutura do esboço para este capítulo'),
      'brainstorm': t('ai.chipBrainstorm', 'Vamos fazer um brainstorm de ideias para este capítulo. Que direções a história poderia tomar?'),
      'expand_chapter': t('ai.chipExpandChapter', 'Sugira maneiras de expandir este capítulo com mais detalhes e profundidade'),
      'check_consistency': t('ai.chipCheckConsistency', 'Verifique a consistência deste capítulo com o restante do livro'),
      'next_step': t('ai.chipNextStep', 'Com base no que escrevi, o que deve acontecer a seguir na história?'),
      // Manga chips
      'manga_fill_page': t('ai.chipMangaFillPage', 'Escreva o roteiro desta página inteira (quadros e falas), avançando a história a partir das páginas anteriores.'),
      'manga_new_character': t('ai.chipMangaNewCharacter', 'Crie um novo personagem para o Universo que faça sentido para esta história.'),
      'manga_what_next': t('ai.chipMangaWhatNext', 'Com base na premissa e nas páginas anteriores, o que deve acontecer nesta página?'),
    };

    const message = messages[chip.action] || chip.label;
    setInput(message);
    inputRef.current?.focus();
  };

  // Auto-apply an action and mark as applied
  const autoApplyAction = useCallback(async (action: AIAction, messageId: string, actionIndex: number) => {
    try {
      // ── Manga actions — reactive fills of the page/Universe/premise ──
      if (action.type === 'manga_page_script') {
        await manga?.onApplyPageScript(action.script);
        applyAction(messageId, actionIndex);
        return;
      }
      if (action.type === 'create_character') {
        await manga?.onCreateCharacter({ name: action.name, characterType: action.characterType, description: action.description });
        applyAction(messageId, actionIndex);
        return;
      }
      if (action.type === 'set_premise') {
        await manga?.onSetPremise(action.premise);
        applyAction(messageId, actionIndex);
        return;
      }

      if (action.type === 'replace' || action.type === 'insert') {
        onApplyAction(action);
        applyAction(messageId, actionIndex);
      } else if (action.type === 'create_chapter') {
        await onCreateChapter?.({
          title: action.title,
          content: action.content,
          position: action.position,
          sectionType: action.sectionType,
        });
        applyAction(messageId, actionIndex);
      } else if (action.type === 'update_chapter') {
        const targetId = action.chapterId === 'current' ? (currentChapterId ?? '') : action.chapterId;
        await onUpdateChapter?.(targetId, { title: action.title });
        applyAction(messageId, actionIndex);
      } else if (action.type === 'delete_chapter') {
        await onDeleteChapter?.(action.chapterId);
        applyAction(messageId, actionIndex);
      } else if (action.type === 'reorder_chapters') {
        await onReorderChapters?.(action.order);
        applyAction(messageId, actionIndex);
      }
    } catch (error) {
      console.error('Auto-apply failed:', error);
    }
  }, [onApplyAction, applyAction, onCreateChapter, onUpdateChapter, onDeleteChapter, onReorderChapters, currentChapterId, manga]);

  // Send text message
  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    addMessage({ role: 'user', content: trimmedInput });
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.sendAIChat({
        message: trimmedInput,
        chapterContent: manga ? '' : chapterContent.slice(0, 50000),
        selection: manga ? undefined : selection,
        projectId,
        currentChapterId,
        history: buildHistory(),
        model: AI_MODEL_MAP[aiQuality],
        ...(manga ? {
          mangaMode: true,
          mangaContext: {
            pageScript: manga.pageScript.slice(0, 20000),
            pageNumber: manga.pageNumber,
            totalPages: manga.totalPages,
            cast: manga.castNames.join('\n').slice(0, 6000),
            premise: manga.premise.slice(0, 6000),
          },
        } : {}),
      });

      const assistantMsg = addMessage({
        role: 'assistant',
        content: response.response,
        actions: (response.actions || []) as AIAction[],
      }) as unknown as { id: string } | undefined;

      // Auto-apply all actions
      const actions = (response.actions || []) as AIAction[];
      if (actions.length > 0 && assistantMsg) {
        for (let i = 0; i < actions.length; i++) {
          await autoApplyAction(actions[i], assistantMsg.id, i);
        }
        if (actions.length === 1) {
          notify.success(t('ai.actionApplied', 'Aplicado'));
        } else {
          notify.success(t('ai.actionsApplied', '{{count}} ações aplicadas', { count: actions.length }));
        }
      }
    } catch (error: unknown) {
      const status = (error as Record<string, unknown>)?.status;
      if (status === 429) {
        notify.error(t('ai.quotaExceeded', 'Cota de IA excedida'));
      } else {
        notify.error(t('ai.chatFailed', 'Falha ao enviar mensagem'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  // Start recording (tap-to-start)
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        await sendAudioMessage(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingSeconds(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch (error) {
      notify.error(t('ai.microphoneError', 'Não foi possível acessar o microfone'));
    }
  };

  // Stop recording (tap-to-stop)
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Toggle recording on/off with a single tap
  const handleDictationClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Send audio message: transcribe + ghostwriter response
  const sendAudioMessage = async (audioBlob: Blob) => {
    setIsLoading(true);
    setIsTranscribing(true);
    // No placeholder message — isTranscribing drives the loading indicator text

    try {
      const response = await api.sendAIChatAudio({
        audio: audioBlob,
        chapterContent: manga ? '' : chapterContent.slice(0, 50000),
        selection: manga ? undefined : selection,
        projectId,
        currentChapterId,
        history: buildHistory(),
        model: AI_MODEL_MAP[aiQuality],
        ...(manga ? {
          mangaMode: true,
          mangaContext: {
            pageScript: manga.pageScript.slice(0, 20000),
            pageNumber: manga.pageNumber,
            totalPages: manga.totalPages,
            cast: manga.castNames.join('\n').slice(0, 6000),
            premise: manga.premise.slice(0, 6000),
          },
        } : {}),
      });

      setIsTranscribing(false);

      // Show transcribed text as the user's message first, then the AI response
      const transcribedText = response.transcribedMessage || t('ai.audioMessage', '[Áudio]');
      addMessage({
        role: 'user',
        content: transcribedText,
        audioUrl: URL.createObjectURL(audioBlob),
      });
      const assistantMessage = addMessage({
        role: 'assistant',
        content: response.response,
        actions: (response.actions || []) as AIAction[],
      }) as unknown as { id: string } | undefined;

      // Auto-apply all actions
      const actions = (response.actions || []) as AIAction[];
      if (actions.length > 0 && assistantMessage) {
        for (let i = 0; i < actions.length; i++) {
          await autoApplyAction(actions[i], assistantMessage.id, i);
        }
        if (actions.length === 1) {
          notify.success(t('ai.actionApplied', 'Aplicado'));
        } else {
          notify.success(t('ai.actionsApplied', '{{count}} ações aplicadas', { count: actions.length }));
        }
      }
    } catch (error: unknown) {
      setIsTranscribing(false);
      const status = (error as Record<string, unknown>)?.status;
      if (status === 429) {
        notify.error(t('ai.quotaExceeded', 'Cota de IA excedida'));
      } else {
        notify.error(t('ai.audioFailed', 'Falha ao processar áudio'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle apply action
  const handleApplyAction = async (message: ChatMessage, actionIndex: number) => {
    if (!message.actions || !message.actions[actionIndex]) return;
    const action = message.actions[actionIndex];

    try {
      // ── Manga actions ──
      if (action.type === 'manga_page_script') {
        await manga?.onApplyPageScript(action.script);
        applyAction(message.id, actionIndex);
        notify.success(t('ai.mangaPageApplied', 'Página preenchida'));
        return;
      }
      if (action.type === 'create_character') {
        await manga?.onCreateCharacter({ name: action.name, characterType: action.characterType, description: action.description });
        applyAction(message.id, actionIndex);
        notify.success(t('ai.mangaCharacterCreated', 'Personagem criado'));
        return;
      }
      if (action.type === 'set_premise') {
        await manga?.onSetPremise(action.premise);
        applyAction(message.id, actionIndex);
        notify.success(t('ai.mangaPremiseSet', 'Premissa definida'));
        return;
      }

      if (action.type === 'replace' || action.type === 'insert') {
        onApplyAction(action);
        applyAction(message.id, actionIndex);
        notify.success(t('ai.actionApplied', 'Aplicado'));
        return;
      }

      if (action.type === 'create_chapter') {
        await onCreateChapter?.({
          title: action.title,
          content: action.content,
          position: action.position,
          sectionType: action.sectionType,
        });
        applyAction(message.id, actionIndex);
        notify.success(t('ai.chapterCreated', 'Capítulo criado'));
      } else if (action.type === 'update_chapter') {
        const targetId = action.chapterId === 'current' ? (currentChapterId ?? '') : action.chapterId;
        await onUpdateChapter?.(targetId, { title: action.title });
        applyAction(message.id, actionIndex);
        notify.success(t('ai.chapterUpdated', 'Capítulo atualizado'));
      } else if (action.type === 'delete_chapter') {
        await onDeleteChapter?.(action.chapterId);
        applyAction(message.id, actionIndex);
        notify.success(t('ai.chapterDeleted', 'Capítulo excluído'));
      } else if (action.type === 'reorder_chapters') {
        await onReorderChapters?.(action.order);
        applyAction(message.id, actionIndex);
        notify.success(t('ai.chaptersReordered', 'Reorganizado'));
      }
    } catch (error) {
      notify.error(t('ai.actionFailed', 'Falha ao aplicar'));
    }
  };

  // Handle revert action
  const handleRevertAction = (message: ChatMessage, actionIndex: number) => {
    if (!message.actions || !message.actions[actionIndex]) return;
    const action = message.actions[actionIndex];

    if (action.type === 'replace' && action.searchText && action.newText) {
      onApplyAction({
        type: 'replace',
        searchText: action.newText,
        newText: action.searchText,
      });
    }

    revertAction(message.id, actionIndex);
    notify.success(t('ai.actionReverted', 'Revertido'));
  };

  // Copy text
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    notify.success(t('common.copied', 'Copiado'));
  };

  // Format time
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Quick actions config
  const quickActions = [
    { id: 'improve' as const, icon: Wand2, label: t('ai.improve', 'Melhorar') },
    { id: 'expand' as const, icon: Expand, label: t('ai.expand', 'Expandir') },
    { id: 'condense' as const, icon: Shrink, label: t('ai.condense', 'Condensar') },
    { id: 'grammar' as const, icon: CheckCircle, label: t('ai.grammar', 'Gramática') },
  ];

  // Shared inner panel content — rendered inside the vaul Drawer (embedded) OR the
  // desktop side-panel motion.div. The Drawer provides overlay, drag-handle, and
  // animation; the desktop branch handles its own slide-in animation.
  const inner = (<>
    {/* Header */}
      <div className="flex flex-col border-b border-border/60 flex-shrink-0">
        <div className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-2.5">
            <span className="text-[15px] leading-none" style={{ color: 'var(--ember-hot)' }} aria-hidden>✦</span>
            <span className="font-serif font-medium text-sm">{t('ai.ghostwriter', 'Ghostwriter')}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={clearMessages}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={t('ai.clearChat', 'Limpar')}
            >
              <Trash2 size={16} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        {/* AI quality toggle */}
        <div className="px-4 pb-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">{t('aiChat.quality', 'Qualidade:')}</span>
          <div className="flex items-center rounded-lg bg-muted border border-border overflow-hidden text-xs">
            {(['standard', 'premium'] as AIQuality[]).map((tier) => (
              <button
                key={tier}
                onClick={() => setAiQuality(tier)}
                title={tier === 'premium' ? t('aiChat.premiumQualityHint', 'Premium usa um modelo superior para melhor prosa — consome mais do seu orçamento de IA.') : t('aiChat.standardQualityHint', 'Modelo padrão — rápido e econômico.')}
                className={cn(
                  'px-2.5 py-1 transition-colors font-medium',
                  aiQuality === tier
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                )}
              >
                {tier === 'standard' ? t('aiChat.standard', 'Padrão') : t('aiChat.premium', 'Premium')}
              </button>
            ))}
          </div>
        </div>
        {/* Status — manga shows the current page/panel context; book shows chapters/words */}
        <div className="px-4 pb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          {manga ? (
            <>
              <Check size={12} className="text-[color:var(--aurora)]" />
              <span>{t('ai.mangaPageOf', 'Página {{n}} de {{total}}', { n: manga.pageNumber, total: manga.totalPages })}{manga.castNames.length ? ` • ${t('ai.mangaCastCount', '{{count}} no elenco', { count: manga.castNames.length })}` : ''}</span>
            </>
          ) : isAnalyzing ? (
            <>
              <RefreshCw size={12} className="animate-spin text-primary" />
              <span>{t('ai.analyzing', 'Analisando {{count}} capítulos...', { count: bookStats.chapters })}</span>
            </>
          ) : (
            <>
              <Check size={12} className="text-[color:var(--aurora)]" />
              <span>{bookStats.chapters} {t('projects.chapters', 'capítulos')} • {bookStats.formattedWords} {t('projects.words', 'palavras')}</span>
            </>
          )}
        </div>
      </div>

      {/* Selection Context + Quick Actions */}
      {selection && (
        <div className="px-4 py-3 flex-shrink-0 border-b border-border/50">
          {/* Selected text preview - Apple/Notion style */}
          <div className="selected-text-preview mb-3 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-transparent border-l-3 border-l-primary pl-3 pr-4 py-2.5">
              <p className="text-sm text-foreground/80 italic line-clamp-3 leading-relaxed">
                "{selection}"
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {quickActions.map((action) => (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action.id)}
                disabled={!!quickActionLoading}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all',
                  'bg-background border border-border hover:border-primary/50 hover:bg-primary/5',
                  quickActionLoading === action.id && 'bg-primary/10 border-primary'
                )}
              >
                {quickActionLoading === action.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <action.icon size={12} />
                )}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area — flex-1 min-h-0 so the drawer cap does the limiting, not a hard max-h */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Empty State */}
          {messages.length === 0 && (
            <div className="flex flex-col items-center text-center pt-10 pb-4 px-4">
              <div className="mb-3 text-muted-foreground/40">
                <Feather size={32} strokeWidth={1.5} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50 mb-2">
                {t('ai.ghostwriter', 'AI Ghostwriter')}
              </p>
              <h3 className="font-serif text-lg text-foreground leading-snug mb-1.5 max-w-[220px]">
                {selection
                  ? t('ai.chatWelcomeSelection', 'O que fazer com esse trecho?')
                  : t('ai.chatWelcome', 'Como posso ajudar?')
                }
              </h3>
              <p className="text-xs text-muted-foreground max-w-[220px] leading-relaxed">
                {selection
                  ? t('ai.chatHintSelection', 'Use as ações rápidas acima ou me faça qualquer pergunta sobre o texto selecionado.')
                  : t('ai.chatHint', 'Peça para melhorar, expandir, reescrever ou gerar o próximo parágrafo.')
                }
              </p>
            </div>
          )}

          {/* Messages */}
          <AnimatePresence mode="popLayout">
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={cn('flex gap-2.5', message.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {message.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot size={14} className="text-primary" />
                  </div>
                )}

                <div className={cn(
                  'max-w-[280px] rounded-2xl px-3.5 py-2.5',
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-lg'
                    : 'bg-muted rounded-bl-lg'
                )}>
                  {/* Audio indicator */}
                  {message.audioUrl && (
                    <div className="flex items-center gap-1.5 mb-1.5 text-xs opacity-70">
                      <Mic size={10} />
                      <span>{t('aiChat.voice', 'Voz')}</span>
                    </div>
                  )}

                  {/* Content */}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>

                  {/* Action Cards */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.actions.map((action, index) => (
                        <div
                          key={index}
                          className="p-2.5 rounded-xl bg-background/60 border border-border/50"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                              <Sparkles size={10} />
                              {action.type === 'replace' && t('ai.actionReplace', 'Substituir texto')}
                              {action.type === 'insert' && t('ai.actionInsert', 'Inserir texto')}
                              {action.type === 'create_chapter' && t('ai.actionCreateChapter', 'Criar capítulo')}
                              {action.type === 'update_chapter' && t('ai.actionUpdateChapter', 'Atualizar capítulo')}
                              {action.type === 'delete_chapter' && t('ai.actionDeleteChapter', 'Excluir capítulo')}
                              {action.type === 'reorder_chapters' && t('ai.actionReorderChapters', 'Reorganizar')}
                              {action.type === 'manga_page_script' && t('ai.actionMangaPageScript', 'Preencher página')}
                              {action.type === 'create_character' && t('ai.actionCreateCharacter', 'Novo no Universo')}
                              {action.type === 'set_premise' && t('ai.actionSetPremise', 'Definir premissa')}
                            </div>
                            {action.applied && (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-[color:var(--aurora)]">
                                <Check size={10} />
                                {t('ai.applied', 'Aplicado')}
                              </span>
                            )}
                          </div>

                          {/* Text preview */}
                          {action.type === 'replace' && action.searchText && (
                            <p className="text-xs line-through opacity-50 mb-1 line-clamp-1">{action.searchText}</p>
                          )}
                          {(action.type === 'replace' || action.type === 'insert') && action.newText && (
                            <p className="text-xs text-foreground line-clamp-2 mb-2">{action.newText}</p>
                          )}

                          {/* Chapter preview */}
                          {action.type === 'create_chapter' && (
                            <p className="text-xs text-foreground mb-2">{action.title}</p>
                          )}

                          {/* Manga previews */}
                          {action.type === 'manga_page_script' && (
                            <pre className="text-[11px] text-foreground/80 whitespace-pre-wrap line-clamp-4 mb-2 font-mono leading-relaxed">{action.script}</pre>
                          )}
                          {action.type === 'create_character' && (
                            <p className="text-xs text-foreground mb-2">
                              <span className="font-medium">{action.name}</span>
                              {action.description ? <span className="text-muted-foreground"> — {action.description}</span> : null}
                            </p>
                          )}
                          {action.type === 'set_premise' && (
                            <p className="text-xs text-foreground line-clamp-3 mb-2">{action.premise}</p>
                          )}

                          {/* Buttons */}
                          <div className="flex items-center gap-1.5">
                            {!action.applied ? (
                              <button
                                onClick={() => handleApplyAction(message, index)}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                              >
                                <Check size={12} />
                                {t('ai.apply', 'Aplicar')}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRevertAction(message, index)}
                                disabled={action.type !== 'replace'}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors disabled:opacity-50"
                              >
                                <Undo2 size={12} />
                                {t('ai.revert', 'Desfazer')}
                              </button>
                            )}
                            {(action.type === 'replace' || action.type === 'insert') && action.newText && (
                              <button
                                onClick={() => handleCopy(action.newText || '')}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                              >
                                <Copy size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <p className={cn(
                    'text-[10px] mt-1.5 opacity-60',
                    message.role === 'user' ? 'text-right' : 'text-left'
                  )}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>

                {message.role === 'user' && (
                  <div className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User size={14} className="text-accent-foreground" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Loading indicator */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-2.5"
            >
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bot size={14} className="text-primary" />
              </div>
              <div className="bg-muted rounded-2xl rounded-bl-lg px-3.5 py-2.5">
                <div className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    {isTranscribing
                      ? t('ai.transcribing', 'Transcrevendo...')
                      : t('ai.thinking', 'Pensando...')}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-border bg-background p-3">
        {/* Voice Dictation Button — primary ghostwriter CTA */}
        <button
          onClick={handleDictationClick}
          disabled={isLoading && !isRecording}
          className={cn(
            'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium text-sm transition-all mb-2.5',
            isRecording
              ? 'bg-red-500 text-white hover:bg-red-600 shadow-md shadow-red-500/30'
              : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
          )}
        >
          {isRecording ? (
            <>
              <span className="w-2 h-2 rounded-full bg-white animate-pulse flex-shrink-0" />
              <span>{t('ai.recording', 'Gravando')} • {formatRecordingTime(recordingSeconds)}</span>
              <MicOff size={15} className="ml-auto" />
            </>
          ) : (
            <>
              <Mic size={15} />
              <span>{t('ai.dictateIdea', 'Ditar ideia')}</span>
            </>
          )}
        </button>

        {/* Input row */}
        <div className="flex items-end gap-2">
          {/* Text input with send button inside */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={selection
                ? t('ai.typeMessageSelection', 'O que fazer com este trecho?')
                : t('ai.typeMessage', 'Escreva algo ou faça uma pergunta…')
              }
              disabled={isLoading || isRecording}
              rows={1}
              className={cn(
                'w-full resize-none rounded-[21px] px-4 py-2.5 pr-12 text-sm',
                'bg-muted border-0 shadow-[0_0_0_1px_var(--border)_inset]',
                'focus:outline-none focus:ring-2 focus:ring-primary/30',
                'placeholder:text-muted-foreground/60',
                'disabled:opacity-50'
              )}
              style={{ minHeight: '42px', maxHeight: '120px' }}
            />
            {/* Send button anchored to bottom-right of the textarea */}
            <div className="absolute right-1.5 bottom-1.5">
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className={cn(
                  'p-2 rounded-full transition-all',
                  input.trim()
                    ? 'text-[#141005] shadow-[0_0_14px_rgba(223,201,147,0.5)]'
                    : 'text-muted-foreground/50'
                )}
                style={input.trim() ? { background: 'linear-gradient(135deg, var(--ember), #C4A96B)' } : undefined}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Contextual Suggestions */}
        {!selection && (
          <div className="mt-2.5 pt-2.5 border-t border-border/50">
            <div className="flex items-center gap-1 mb-2">
              <Lightbulb size={12} className="text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                {t('ai.suggestions', 'Sugestões')}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {contextualChips.map((chip) => (
                <button
                  key={chip.id}
                  onClick={() => handleChipClick(chip)}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs',
                    'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground',
                    'border border-transparent hover:border-border',
                    'transition-all'
                  )}
                  title={chip.description}
                >
                  <chip.icon size={12} />
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
  </>);

  if (embedded) {
    // Mobile: vaul DrawerContent provides the overlay, drag-handle and animation.
    // flex-1 min-h-0 lets the inner layout participate in the cap set on DrawerContent
    // (max-h-[90dvh]). The messages area (flex-1 min-h-0 overflow-y-auto) then scrolls
    // within whatever space remains after the header and input are laid out.
    return (
      <div className="flex flex-col flex-1 min-h-0 bg-card">
        {inner}
      </div>
    );
  }

  // Desktop: slide-in side panel from the right edge.
  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-full w-[340px] flex-shrink-0 border-l flex flex-col overflow-hidden border-border/60 bg-background"
    >
      {inner}
    </motion.div>
  );
}
